import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { Inject } from '@syncfusion/ej2-react-base';
import {
    Annotation,
    FormDesigner,
    FormFieldModel,
    FormFields,
    Magnification,
    Navigation,
    PageRenderCompleteEventArgs,
    PdfViewerComponent,
    Toolbar,
} from '@syncfusion/ej2-react-pdfviewer';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../../api/features/features';
import { useFormView } from '../../../contexts/useFormView';
import { useFormHasEditableFields } from '../../../hooks/useFormHasEditableFields';
import { useIsCollaborationUIEnabled } from '../../../hooks/useIsCollaborationUIEnabled';
import { TaxReturnTOCKey, ViewFormResponseFormFields } from '../../../model';
import { POSTHOG_NO_CAPTURE_CLASS } from '../../../telemetry/PostHogNoCapture';
import {
    CalcReviewEnhancedDescriptiveDataEnabled,
    CalcReviewFormCommentsEnabled,
    DetailedWorkspacesEnabled,
    EditFormViewDescriptiveDataEnabled,
    FormsViewDrilldownEnabled,
} from '../../../utils/FeatureFlags.constants';
import { applyFormZoomSettings } from '../../../utils/FormZoomSettings';
import {
    IInteractiveFormField,
    removeAllInteractiveFieldHitboxes,
    syncInteractiveFieldHitboxes,
} from '../../../utils/InteractiveFieldHitboxes';
import { attemptPdfGoToPage } from '../../../utils/pdfViewerSync';
import {
    createStatementIconOverlay,
    flattenPdfFormFields,
    focusElementAfterFormModal,
    getFieldsDictionary,
    getOverridableFieldCommentState,
    removeAllStatementIcons,
    updateAllStatementIconPositions,
} from '../../../utils/PdfViewerUtils';
import { SUBTOTAL_COLUMN_ID, TOTAL_COLUMN_ID } from '../../../utils/ReportUtils';
import { findTocItemById, getTocItemKey, getTocItemPagesAndParentId } from '../../../utils/TaxReturnTOCUtils';
import PdfContextualMenu from '../../pdf-contextual-menu/PdfContextualMenu';

const useStyles = makeStyles({
    error: {
        padding: '0.5rem',
    },
    pdfViewer: {
        height: '100%',
        minHeight: '200px !important',
        '& .e-pv-main-container': {
            minHeight: '200px !important',
        },
        '& .foreign-object': {
            backgroundColor: 'white',
        },
        '& .e-pv-formfield-input:focus': {
            border: '2px solid #222222 !important',
            borderRadius: '4px !important',
        },
        '& .e-pv-checkbox-div:focus': {
            border: '2px solid #222222 !important',
            borderRadius: '4px !important',
        },
        '& [data-interactive-field-hitbox]:focus': {
            border: '2px solid #222222 !important',
            borderRadius: '4px !important',
            outline: 'none',
        },
    },
    pdfViewerVisible: {
        display: 'flex',
    },
    pdfViewerHidden: {
        display: 'none',
    },
    pdfContainer: {
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
    },
    pdfContainerVisible: {
        display: 'flex',
    },
    pdfContainerHidden: {
        display: 'none',
    },
});

export interface IPdfViewerFormProps {
    pdfData: string | null;
    isLoading?: boolean;
    showPdf?: boolean;
    onPageChange: (page: any) => void;
    onZoomChange: (zoom: any) => void;
    width: string;
    formFields?: ViewFormResponseFormFields;
}

interface IPdfViewerMagnificationModule {
    reRenderPageNumber: number;
    isPagesZoomed: boolean;
    magnifyPages(): void;
}

interface IPdfViewerWithMagnificationInternals {
    magnificationModule?: IPdfViewerMagnificationModule;
}

const DefaultPDFViewer = React.forwardRef(
    (
        { pdfData, isLoading = false, showPdf = false, onPageChange = () => {}, onZoomChange, width, formFields }: IPdfViewerFormProps,
        ref
    ) => {
        const styles = useStyles();
        const pdfViewer = useRef<PdfViewerComponent>(null);
        const componentRef = useRef(null);
        const {
            currentTOCItemState,
            formSettings,
            currentWhitePaperReport,
            hasError,
            updateCurrentFormTotalPages,
            setPdfViewerLoadedTocItemId,
            pdfSyncPhase,
            setPdfSyncPhase,
            pdfNavigationRequestId,
            tocData,
            tocReferenceMap,
            dataNameToTocItemMap,
            emptyDetailedWorkspaceItemsMap,
            loadStatementDataForModal,
            selectedEntity,
            elementToFocusAfterFormModal,
            setElementToFocusAfterFormModal,
            commentMetadataVersion,
        } = useFormView();
        const [openAmountFieldMenu, setOpenAmountFieldMenu] = useState(false);
        const [openDescriptiveDataFieldMenu, setOpenDescriptiveDataFieldMenu] = useState(false);
        const [openOverridableFieldMenu, setOpenOverridableFieldMenu] = useState(false);
        const [isOverridableContextTarget, setIsOverridableContextTarget] = useState(false);
        const isCollaborationUIEnabled = useIsCollaborationUIEnabled();
        const { data: isCalcReviewFormCommentsEnabled } = useIsFeatureEnabled({
            featureFlagName: CalcReviewFormCommentsEnabled,
        });
        const hasEditableFields = useFormHasEditableFields(formFields);
        const isFormFieldCommentsEnabled = isCollaborationUIEnabled && !!isCalcReviewFormCommentsEnabled && hasEditableFields;
        const [IsResourcesLoaded, { setTrue: setIsResourcesLoaded }] = useBoolean(false);
        const [isPdfDocumentLoaded, setIsPdfDocumentLoaded] = useState(false);
        const pendingPostLoadSetupRef = useRef(false);
        const loadedPdfDataRef = useRef<string | null>(null);
        const cancelGoToPageRef = useRef<(() => void) | null>(null);
        const syncFallbackTimeoutRef = useRef<number | null>(null);
        const isPdfDocumentLoadedRef = useRef(isPdfDocumentLoaded);
        isPdfDocumentLoadedRef.current = isPdfDocumentLoaded;
        const formSettingsRef = useRef(formSettings);
        formSettingsRef.current = formSettings;
        const currentTOCItemStateRef = useRef(currentTOCItemState);
        currentTOCItemStateRef.current = currentTOCItemState;
        const pdfSyncPhaseRef = useRef(pdfSyncPhase);
        pdfSyncPhaseRef.current = pdfSyncPhase;
        const expectedPageNumberRef = useRef<number>(1);
        const { t } = useTranslation();
        const [contextMenuTarget, setContextMenuTarget] = useState<FormFieldModel | undefined>(undefined);
        const interactiveFieldsRef = useRef<IInteractiveFormField[]>([]);

        const { data: isFormsViewDrilldownEnabled, isFetching: isFetchingFormsViewDrilldownFeatureFlag } = useIsFeatureEnabled({
            featureFlagName: FormsViewDrilldownEnabled,
        });

        const { data: isEditFormViewDescriptiveDataEnabled } = useIsFeatureEnabled({
            featureFlagName: EditFormViewDescriptiveDataEnabled,
        });

        const { data: isDetailedWorkspacesEnabled } = useIsFeatureEnabled({
            featureFlagName: DetailedWorkspacesEnabled,
        });

        const { data: isEnhancedDescriptiveDataEnabled } = useIsFeatureEnabled({
            featureFlagName: CalcReviewEnhancedDescriptiveDataEnabled,
        });

        useImperativeHandle(ref, () => pdfViewer.current);

        const services = (
            <Inject
                services={[
                    Toolbar,
                    Navigation,
                    Magnification,
                    Annotation,
                    ...(isFormsViewDrilldownEnabled ? [FormFields, FormDesigner] : []),
                ]}
            />
        );

        const completePageSync = (_page: { currentPageNumber: number }) => {
            if (syncFallbackTimeoutRef.current !== null) {
                window.clearTimeout(syncFallbackTimeoutRef.current);
                syncFallbackTimeoutRef.current = null;
            }

            const loadedTocItemId = currentTOCItemStateRef.current?.id;
            if (loadedTocItemId) {
                setPdfViewerLoadedTocItemId(loadedTocItemId);
            }
            setPdfSyncPhase('ready');
        };

        const scheduleSyncFallback = () => {
            if (syncFallbackTimeoutRef.current !== null) {
                window.clearTimeout(syncFallbackTimeoutRef.current);
            }

            syncFallbackTimeoutRef.current = window.setTimeout(() => {
                syncFallbackTimeoutRef.current = null;
                if (pdfSyncPhaseRef.current !== 'syncing-page') {
                    return;
                }

                if (pdfViewer.current?.currentPageNumber === expectedPageNumberRef.current) {
                    completePageSync({ currentPageNumber: expectedPageNumberRef.current });
                }
            }, 400);
        };

        const syncPdfToPage = (pageIndex: number) => {
            expectedPageNumberRef.current = pageIndex + 1;

            if (pdfViewer.current?.currentPageNumber === expectedPageNumberRef.current) {
                completePageSync({ currentPageNumber: expectedPageNumberRef.current });
                return;
            }

            setPdfSyncPhase('syncing-page');

            if (cancelGoToPageRef.current) {
                cancelGoToPageRef.current();
            }

            scheduleSyncFallback();

            cancelGoToPageRef.current = attemptPdfGoToPage(pdfViewer.current, pageIndex, () => {
                if (pdfViewer.current?.currentPageNumber === expectedPageNumberRef.current) {
                    completePageSync({ currentPageNumber: expectedPageNumberRef.current });
                }
            });
        };

        const handleStatementIconClick = (fieldDataName: string, formFieldElement?: HTMLElement) => {
            if ((!tocReferenceMap && !emptyDetailedWorkspaceItemsMap) || !fieldDataName) {
                return;
            }

            const formFieldName = formFieldElement?.getAttribute('name') ?? '';
            const isEmptyDetailedWorkspace = emptyDetailedWorkspaceItemsMap.has(fieldDataName);
            if (isEmptyDetailedWorkspace) {
                const emptyTocItem = emptyDetailedWorkspaceItemsMap.get(fieldDataName);
                if (emptyTocItem) {
                    loadStatementDataForModal([emptyTocItem], selectedEntity, formFieldName, true);
                }
                return;
            }

            const tocItem = dataNameToTocItemMap.get(fieldDataName);

            if (!tocItem) {
                return;
            }

            const tocItemKey: TaxReturnTOCKey | undefined = getTocItemKey(tocItem);
            const parentTocItemKey: TaxReturnTOCKey | undefined = tocItem.parentId
                ? getTocItemKey(findTocItemById(tocData?.items, tocItem.parentId))
                : undefined;
            const { formPages } = getTocItemPagesAndParentId(tocReferenceMap, tocItemKey, parentTocItemKey);

            if (formPages && formPages.length > 0) {
                loadStatementDataForModal(formPages, selectedEntity, formFieldName);
            }

            if (formFieldElement) {
                setElementToFocusAfterFormModal(formFieldName);
            }
        };

        const handleHitboxContextMenu = (field: IInteractiveFormField, element: HTMLElement, event: MouseEvent) => {
            const column = currentWhitePaperReport?.columns?.[selectedEntity?.index ?? 0];
            const isExcludedColumn = column?.originalColumnId === TOTAL_COLUMN_ID || column?.originalColumnId === SUBTOTAL_COLUMN_ID;
            const contextMenuField = { id: element.id, name: field.name } as FormFieldModel;
            setIsOverridableContextTarget(isFormFieldCommentsEnabled && field.overridable && !isExcludedColumn);

            // preventDefault only when an app menu opens — on excluded columns the browser's
            // default context menu should still appear (matching the pre-hitbox behavior).
            if (field.isDrilldown && !isExcludedColumn) {
                event.preventDefault();
                handleAmountFieldRightClick(contextMenuField);
            } else if (field.isDescriptiveData) {
                event.preventDefault();
                handleDescriptiveDataFieldRightClick(contextMenuField);
            } else if (isFormFieldCommentsEnabled && field.overridable && !isExcludedColumn) {
                event.preventDefault();
                handleOverridableFieldRightClick(contextMenuField);
            }
        };

        const handleOverridableFieldRightClick = (field: FormFieldModel) => {
            setContextMenuTarget(field);
            !openOverridableFieldMenu && setOpenOverridableFieldMenu(true);
        };

        // Text fields are flattened, but checkboxes/radios still render as live Syncfusion
        // inputs protected only by the PDF-level readOnly flag. Reapply the DOM-level guard
        // the old form-field wiring provided so keyboard activation cannot toggle the
        // displayed state.
        const guardLiveFormFieldElements = () => {
            pdfViewer.current?.formFieldCollections?.forEach((field: FormFieldModel) => {
                const element = field.id ? document.getElementById(field.id) : null;
                if (!element || element.dataset.readonlyGuardAttached === 'true') {
                    return;
                }
                element.dataset.readonlyGuardAttached = 'true';
                element.setAttribute('readonly', 'true');
                element.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                });
            });
        };

        const syncInteractiveFields = () => {
            if (!pdfViewer.current || !currentWhitePaperReport) {
                return;
            }

            guardLiveFormFieldElements();
            syncInteractiveFieldHitboxes('formViewerPDF', interactiveFieldsRef.current, {
                onContextMenu: handleHitboxContextMenu,
                onStatementFieldElement: (field, element) => {
                    if (!isDetailedWorkspacesEnabled) {
                        return;
                    }
                    createStatementIconOverlay(
                        element,
                        element.id,
                        { fieldDataName: field.dataName },
                        dataNameToTocItemMap,
                        emptyDetailedWorkspaceItemsMap,
                        handleStatementIconClick
                    );
                },
            });
            // No hitbox borders: every border — including the interactive light-blue
            // signifier (PBI 4693069) — is authored into the PDF by the form generation
            // (ReviewFormsService appearance callback, driven by the row sets the form
            // request carries), so it stacks in correct z-order with overlapping fields.
        };

        // currentWhitePaperReport's cells are mutated in place whenever report comment metadata
        // resolves (see DataManagerUtils), so the object reference never changes and the main
        // flatten effect below (keyed on pdfData/currentWhitePaperReport) never re-runs for it.
        // commentMetadataVersion is the signal that something changed; re-derive each field's
        // comment state - including overridable itself, not just the comment counts - from the
        // now-current report/entity/collaboration state and reapply the indicator classes,
        // without re-flattening/reloading the PDF itself. overridable has to be re-derived here
        // too (not skipped when already false) because selectedEntity can resolve a field to a
        // different report column on every entity switch, and syncInteractiveFieldHitboxes reads
        // the field this function hands it fresh each time - see hitboxFields there for how the
        // context-menu listener picks up the change on an already-created hitbox.
        const refreshCommentIndicators = () => {
            if (!interactiveFieldsRef.current.length) {
                return;
            }

            // getOverridableFieldCommentState's first check is on matchedField?.overridable itself -
            // passing the already-derived IInteractiveFormField back in would feed it its own prior
            // output, so once a field is baked as overridable: false it could never be re-derived as
            // true (e.g. once isFormFieldCommentsEnabled catches up after the initial flatten). Re-look
            // up the real backend field so overridable is always re-derived from source.
            const fieldsDictionary = getFieldsDictionary(formFields);

            interactiveFieldsRef.current = interactiveFieldsRef.current.map((field) => {
                const { overridable, hasUnresolvedComments, hasOnlyResolvedComments, cellDisplayValue } = getOverridableFieldCommentState(
                    fieldsDictionary[field.name],
                    isFormFieldCommentsEnabled,
                    currentWhitePaperReport ?? undefined,
                    selectedEntity?.index
                );

                return { ...field, overridable, hasUnresolvedComments, hasOnlyResolvedComments, cellDisplayValue };
            });

            syncInteractiveFields();
        };

        const handleDescriptiveDataFieldRightClick = (field: FormFieldModel) => {
            setContextMenuTarget(field);
            !openDescriptiveDataFieldMenu && setOpenDescriptiveDataFieldMenu(true);
        };

        const handleAmountFieldRightClick = (field: FormFieldModel) => {
            setContextMenuTarget(field);
            !openAmountFieldMenu && setOpenAmountFieldMenu(true);
        };

        const observeFormFieldChanges = () => {
            const pdfViewerContainer = document.getElementById('formViewerPDF');
            if (!pdfViewerContainer) return;

            const observer = new MutationObserver(() => {
                syncInteractiveFields();
            });

            observer.observe(pdfViewerContainer, {
                childList: true,
                subtree: true,
            });
        };

        const syncZoomFromViewer = () => {
            if (!pdfViewer?.current) {
                return;
            }

            applyFormZoomSettings(pdfViewer.current.magnification, formSettingsRef.current);

            const zoomPercent = Math.round((pdfViewer.current.magnification.zoomFactor ?? 1) * 100);
            onZoomChange({ zoomValue: zoomPercent });
        };

        const forceRemagnifyAtCurrentZoom = (pageNumber: number) => {
            if (!pdfViewer?.current) {
                return;
            }

            applyFormZoomSettings(pdfViewer.current.magnification, formSettingsRef.current);

            const magnificationModule = (pdfViewer.current as IPdfViewerWithMagnificationInternals).magnificationModule;
            if (magnificationModule) {
                magnificationModule.reRenderPageNumber = pageNumber;
                magnificationModule.isPagesZoomed = false;
                magnificationModule.magnifyPages();
            }

            pdfViewer.current.updateViewerContainer();

            const zoomPercent = Math.round((pdfViewer.current.magnification.zoomFactor ?? 1) * 100);
            onZoomChange({ zoomValue: zoomPercent });
        };

        const refreshFitZoomForLayout = () => {
            if (!pdfViewer.current || !isPdfDocumentLoadedRef.current || !pdfData) {
                return;
            }

            const fitType = formSettingsRef.current.zoomFitType;
            if (fitType === 'fitToWidth' || fitType === 'fitToPage') {
                forceRemagnifyAtCurrentZoom(pdfViewer.current.currentPageNumber ?? 1);
            } else {
                pdfViewer.current.updateViewerContainer();
            }

            syncInteractiveFields();
            updateAllStatementIconPositions();
        };

        const onDocumentLoad = () => {
            if (pdfViewer?.current) {
                updateCurrentFormTotalPages(pdfViewer.current.pageCount || 1);

                observeFormFieldChanges();

                if (elementToFocusAfterFormModal) {
                    focusElementAfterFormModal(elementToFocusAfterFormModal);
                    setElementToFocusAfterFormModal(undefined);
                }

                const pageIndex = currentTOCItemStateRef.current?.pageIndex ?? 0;
                syncPdfToPage(pageIndex);

                syncZoomFromViewer();
                setIsPdfDocumentLoaded(true);

                if (!pendingPostLoadSetupRef.current) {
                    return;
                }

                pendingPostLoadSetupRef.current = false;

                window.setTimeout(() => {
                    const remagnifyTargetPageNumber = (currentTOCItemStateRef.current?.pageIndex ?? 0) + 1;
                    if (pdfViewer.current?.currentPageNumber === remagnifyTargetPageNumber) {
                        forceRemagnifyAtCurrentZoom(remagnifyTargetPageNumber);
                    }
                }, 200);
            }
        };

        const pdfViewerClassName = mergeClasses(
            styles.pdfViewer,
            showPdf && !isLoading && !hasError ? styles.pdfViewerVisible : styles.pdfViewerHidden
        );
        const pdfContainerClass = mergeClasses(
            styles.pdfContainer,
            showPdf || isLoading ? styles.pdfContainerVisible : styles.pdfContainerHidden
        );

        const scrollToSelectedPageIndexAndStyleFields = (args: PageRenderCompleteEventArgs) => {
            const pageIndex = currentTOCItemStateRef.current?.pageIndex;
            if (args.data.pageNumber === 1 && pageIndex && pageIndex > args.data.pageNumber - 1) {
                syncPdfToPage(pageIndex);
            }

            syncInteractiveFields();
            updateAllStatementIconPositions();
        };

        useEffect(() => {
            if (!showPdf || !pdfData) {
                return;
            }

            const resizeObserver = new ResizeObserver(() => {
                refreshFitZoomForLayout();
            });

            const elementsToObserve: Element[] = [];
            if (componentRef.current) {
                elementsToObserve.push(componentRef.current);
            }

            const mainContentContainer = document.getElementById('main-content-container');
            if (mainContentContainer) {
                elementsToObserve.push(mainContentContainer);
            }

            elementsToObserve.forEach((element) => resizeObserver.observe(element));

            return () => {
                resizeObserver.disconnect();
            };
        }, [pdfData, width, showPdf]);

        useEffect(() => {
            if (!showPdf || !isPdfDocumentLoaded || !pdfData) {
                return;
            }

            refreshFitZoomForLayout();
        }, [width, showPdf, isPdfDocumentLoaded, pdfData]);

        useEffect(() => {
            const handleScroll = () => {
                updateAllStatementIconPositions();
                setOpenAmountFieldMenu(false);
                setOpenDescriptiveDataFieldMenu(false);
            };

            window.addEventListener('scroll', handleScroll, { capture: true, passive: true });

            return () => {
                window.removeEventListener('scroll', handleScroll, { capture: true });
            };
        }, []);

        useEffect(() => {
            if (IsResourcesLoaded && pdfData && showPdf && currentWhitePaperReport) {
                const isSamePdfAlreadyLoaded = loadedPdfDataRef.current === pdfData && isPdfDocumentLoaded;

                if (isSamePdfAlreadyLoaded) {
                    const pageIndex = currentTOCItemStateRef.current?.pageIndex ?? 0;
                    syncPdfToPage(pageIndex);
                    return;
                }

                loadedPdfDataRef.current = pdfData;

                if (cancelGoToPageRef.current) {
                    cancelGoToPageRef.current();
                    cancelGoToPageRef.current = null;
                }

                pendingPostLoadSetupRef.current = true;
                setIsPdfDocumentLoaded(false);
                setPdfViewerLoadedTocItemId(null);
                setPdfSyncPhase('loading-document');

                removeAllStatementIcons();
                removeAllInteractiveFieldHitboxes('formViewerPDF');

                const { base64Pdf, interactiveFields } = flattenPdfFormFields(pdfData, {
                    formFields,
                    currentWhitePaperReport,
                    isFormsViewDrilldownEnabled: isFormsViewDrilldownEnabled || false,
                    isEditFormViewDescriptiveDataEnabled: isEditFormViewDescriptiveDataEnabled || false,
                    isDetailedWorkspacesEnabled: isDetailedWorkspacesEnabled || false,
                    dataNameToTocItemMap,
                    emptyDetailedWorkspaceItemsMap,
                    checkboxBackgroundColor: '#ffffff',
                    selectedEntity,
                    isEnhancedDescriptiveDataEnabled: isEnhancedDescriptiveDataEnabled || false,
                    selectedEntityIndex: selectedEntity?.index,
                    isFormFieldCommentsEnabled,
                });
                interactiveFieldsRef.current = interactiveFields;

                pdfViewer.current?.load(base64Pdf, '');
            }
        }, [IsResourcesLoaded, pdfData, showPdf, currentWhitePaperReport, formFields]);

        useEffect(() => {
            if (!isPdfDocumentLoaded) {
                return;
            }

            refreshCommentIndicators();
            // isFormFieldCommentsEnabled and selectedEntity?.index are read inside
            // refreshCommentIndicators (via getOverridableFieldCommentState) but weren't deps
            // here - if the claims/feature-flag queries backing isFormFieldCommentsEnabled resolve
            // after the initial flatten, or the user switches entities on a combined multi-entity
            // form, nothing re-derived overridable and every affected field was stuck with whatever
            // the first flatten baked in for the rest of the session.
        }, [commentMetadataVersion, isPdfDocumentLoaded, isFormFieldCommentsEnabled, selectedEntity?.index]);

        useEffect(() => {
            if (!isPdfDocumentLoaded || currentTOCItemState?.pageIndex == null || !pdfViewer.current) {
                return;
            }

            const targetPageNumber = currentTOCItemState.pageIndex + 1;
            if (pdfViewer.current.currentPageNumber === targetPageNumber && pdfSyncPhaseRef.current === 'ready') {
                const tocItemId = currentTOCItemState.id;
                if (tocItemId) {
                    setPdfViewerLoadedTocItemId(tocItemId);
                }
                return;
            }

            syncPdfToPage(currentTOCItemState.pageIndex);
        }, [currentTOCItemState?.id, currentTOCItemState?.pageIndex, isPdfDocumentLoaded, pdfNavigationRequestId]);

        useEffect(() => {
            return () => {
                if (cancelGoToPageRef.current) {
                    cancelGoToPageRef.current();
                }
                if (syncFallbackTimeoutRef.current !== null) {
                    window.clearTimeout(syncFallbackTimeoutRef.current);
                }
            };
        }, []);

        const handlePageChange = (page: { currentPageNumber: number }) => {
            const phase = pdfSyncPhaseRef.current;

            if (phase === 'loading-document') {
                return;
            }

            if (phase === 'syncing-page') {
                if (page.currentPageNumber !== expectedPageNumberRef.current) {
                    return;
                }

                completePageSync(page);
                return;
            }

            if (phase === 'ready') {
                onPageChange(page);
            }
        };

        const onZoomChangeEvent = (zoom: { zoomValue?: number }) => {
            if (!pdfViewer.current || typeof zoom?.zoomValue !== 'number' || !Number.isFinite(zoom.zoomValue)) {
                return;
            }

            onZoomChange({ zoomValue: zoom.zoomValue });
            syncInteractiveFields();
            updateAllStatementIconPositions();
        };

        if (isFetchingFormsViewDrilldownFeatureFlag) {
            return null;
        }

        return (
            <div className={mergeClasses('form-viewer-container', pdfContainerClass, POSTHOG_NO_CAPTURE_CLASS)} ref={componentRef}>
                <PdfViewerComponent
                    ref={pdfViewer}
                    id='formViewerPDF'
                    enableToolbar={false}
                    resourcesLoaded={() => setIsResourcesLoaded()}
                    resourceUrl={`${origin}/ej2-pdfviewer-lib`}
                    enableNavigationToolbar={false}
                    enableNavigation={true}
                    initialRenderPages={10}
                    className={pdfViewerClassName}
                    style={{ width: width || '100%' }}
                    enableThumbnail={false}
                    pageRenderComplete={scrollToSelectedPageIndexAndStyleFields}
                    pageChange={handlePageChange}
                    zoomChange={onZoomChangeEvent}
                    documentLoad={onDocumentLoad}
                    enableFormFields={true}
                    enableFormDesigner={true}
                >
                    {services}
                </PdfViewerComponent>
                {hasError && <div className={styles.error}>{t('pdfDisplayError')}</div>}
                <PdfContextualMenu
                    open={openAmountFieldMenu || openDescriptiveDataFieldMenu || openOverridableFieldMenu}
                    isAmountField={openAmountFieldMenu}
                    IsDescriptiveDataField={openDescriptiveDataFieldMenu}
                    isOverridableField={isOverridableContextTarget}
                    formFields={formFields}
                    setOpenAmountFieldMenu={setOpenAmountFieldMenu}
                    setOpenDescriptiveDataFieldMenu={setOpenDescriptiveDataFieldMenu}
                    setOpenOverridableFieldMenu={setOpenOverridableFieldMenu}
                    contextMenuTarget={contextMenuTarget}
                />
            </div>
        );
    }
);

export default DefaultPDFViewer;
